import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Icon from '../AppIcon';
import Button from './Button';
import { useAuth } from '../../contexts/AuthContext';
import { APP_NAME } from '../../constants';
import { userService, authService } from '../../services/supabaseService';

const RoleBasedHeader = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isMoreMenuOpen, setIsMoreMenuOpen] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState('online');
  const { user, signOut } = useAuth();
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const [userName, setUserName] = useState('');

  // Configuration de navigation par rôle
  const roleNavigation = {
    client: '/customer-account-portal',
    technicien: '/technician-daily-routes',
    dispatch: '/dispatch-control-center',
    admin: '/analytics-dashboard',
    manager: '/service-catalog-manager',
    logistician: '/inventory-management',
    trainer: '/service-catalog-manager',
  };

  // Fonction de navigation principale
  const handleIconNavigation = () => {
    // Si l'utilisateur n'est pas connecté
    if (!currentUser) {
      navigate('/');
      return;
    }

    // Si l'utilisateur est connecté, naviguer selon son rôle
    const role = currentUser.role;
    const defaultPath = roleNavigation[role];
    
    if (defaultPath) {
      navigate(defaultPath);
    } else {
      // Rôle non reconnu, rediriger vers la page d'accueil
      navigate('/');
    }
  };

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 1024) {
        setIsMobileMenuOpen(false);
      }
    };

    const fetchCurrentUser = async () => {
      try {
        setLoading(true);
        const { user: authUser, error: user1Error } = await authService.getCurrentUser();
        console.log('Utilisateur auth:', authUser?.id);

        if (authUser) {
          const { data: userprofile, error: userError } = await userService.getUserProfile(authUser?.id);
          console.log('Profil utilisateur:', userprofile);
          
          if (userError) {
            console.error('Error fetching current user in Header:', userError);
            return;
          }

          if (userprofile) {
            const formattedUser = {
              id: userprofile.id,
              name: userprofile.full_name || authUser.email?.split('@')[0] || 'Utilisateur',
              email: userprofile.email,
              role: userprofile.role || 'client',
              avatar_url: userprofile.avatar_url
            };

            setCurrentUser(formattedUser);
            setUserName(formattedUser.name);
            console.log('✅ Utilisateur connecté:', formattedUser);
          }
        } else {
          console.log('❌ Aucun utilisateur connecté');
          setCurrentUser(null);
          setUserName('');
        }
      } catch (err) {
        console.error('❌ Error fetchCurrentUser:', err);
        setCurrentUser(null);
      } finally {
        setLoading(false);
      }
    };

    fetchCurrentUser();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
    setIsMoreMenuOpen(false);
  }, [location?.pathname, location?.search]);

  // Fermer le menu "Plus" quand on clique ailleurs
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.more-menu-container')) {
        setIsMoreMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const navigationItems = {
    client: [
      { path: '/service-booking-wizard', label: 'Réserver', icon: 'Calendar' },
      {
        path: '/public-service-tracking',
        label: 'Mes interventions',
        labelLines: ['Mes interventions'],
        icon: 'MapPin',
      },
      { path: '/mes-devis', label: 'Mes devis', icon: 'FileText' },
      { path: '/customer-account-portal', label: 'Mon compte', icon: 'User' },
    ],
    technician: [
      { path: '/technician-daily-routes', label: 'Mes interventions', icon: 'Route' },
      { path: '/public-service-tracking', label: 'Suivi en direct', icon: 'MapPin' },
    ],
    dispatcher: [
      { path: '/dispatch-control-center', label: 'Centre de dispatch', icon: 'Radio' },
      { path: '/service-booking-wizard', label: 'Nouvelle demande', icon: 'Plus' },
      { path: '/analytics-dashboard', label: 'Tableau de bord', icon: 'BarChart3' },
    ],
    admin: [
      { path: '/analytics-dashboard', label: 'Analytics', icon: 'BarChart3' },
      { path: '/interventions-management', label: 'Intervention', icon: 'Wrench' },
      { path: '/service-catalog-manager', label: 'Catalogue', icon: 'Package' },
      { path: '/user-management', label: 'Utilisateurs', icon: 'Users' },
      { path: '/inventory-management', label: 'Gestion des stocks', icon: 'Package' },
      { path: '/checklist-service', label: 'Service et points controle', icon: 'Wrench' },
      { path: '/dispatch-control-center', label: 'Dispatch', icon: 'Radio' },
      { path: '/services-zone', label: 'Zones', icon: 'MapPin' },
    ],
    manager: [
      { path: '/service-catalog-manager', label: 'Catalogue', icon: 'Package' },
      { path: '/dispatch-control-center', label: 'Dispatch', icon: 'Radio' },
      { path: '/inventory-management', label: 'Gestion des stocks', icon: 'Package' },
      { path: '/checklist-service', label: 'Service et points controle', icon: 'Wrench' },
      { path: '/interventions-management', label: 'Intervention', icon: 'Wrench' },
      { path: '/services-zone', label: 'Zones', icon: 'MapPin' },
    ],
    logistician: [
      { path: '/inventory-management', label: 'Gestion des stocks', icon: 'Package' },
    ],
    trainer: [
      { path: '/service-catalog-manager', label: 'Catalogue de services', icon: 'Package' },
      { path: '/technician-daily-routes', label: 'Parcours techniciens', icon: 'Route' },
      { path: '/analytics-dashboard', label: 'Rapports', icon: 'FileText' },
    ],
  };

  const clientMobileMenuItems = [
    {
      path: '/service-booking-wizard',
      label: 'Réserver une intervention',
      icon: 'Calendar',
    },
    {
      path: '/public-service-tracking',
      label: 'Mes interventions',
      icon: 'ClipboardList',
    },
    {
      path: '/mes-devis',
      label: 'Mes devis',
      icon: 'FileText',
    },
    {
      path: '/customer-account-portal?section=payments',
      label: 'Mes moyens de paiement',
      icon: 'CreditCard',
    },
    {
      path: '/customer-account-portal?section=communications',
      label: 'Mes communications',
      icon: 'MessageSquare',
    },
    {
      path: '/customer-account-portal?section=settings',
      label: 'Mon profil',
      icon: 'User',
    },
    {
      path: '/customer-account-portal?section=privacy-policy',
      label: 'Politique de confidentialité',
      icon: 'Shield',
    },
  ];

  const isActivePath = (path) => {
    const pathOnly = String(path || '').split('?')[0];
    return location?.pathname === pathOnly;
  };

  const isNavItemActive = (item) => {
    const [pathOnly, query = ''] = String(item?.path || '').split('?');
    if (location?.pathname !== pathOnly) return false;

    const itemParams = new URLSearchParams(query);
    const currentParams = new URLSearchParams(location.search);

    if (pathOnly === '/customer-account-portal') {
      const itemSection = itemParams.get('section');
      if (!itemSection) return true;
      return (currentParams.get('section') || 'settings') === itemSection;
    }

    return true;
  };

  const isClientMobileItemActive = (item) => isNavItemActive(item);

  const currentNavItems = navigationItems?.[currentUser?.role] || [];
  const isClientRole = currentUser?.role === 'client';

  // Séparer les éléments : 3 premiers pour le header + 1 sélectionné à la place du 4ème
  const getMainNavItems = () => {
    // Toujours garder les 3 premiers
    const firstThree = currentNavItems.slice(0, 3);
    
    // Trouver l'élément actif s'il existe
    const activeItem = currentNavItems.find((item) => isNavItemActive(item));
    
    // Si un élément est actif et qu'il n'est pas dans les 3 premiers
    if (activeItem && !firstThree.some((item) => item.path === activeItem.path)) {
      return [...firstThree, activeItem];
    }
    
    // Sinon, prendre les 4 premiers
    return currentNavItems.slice(0, 4);
  };

  const mainNavItems = getMainNavItems();
  
  // Les éléments supplémentaires sont tous ceux qui ne sont pas dans mainNavItems
  const moreNavItems = currentNavItems.filter(
    (item) => !mainNavItems.some((mainItem) => mainItem.path === item.path)
  );

  const handleNavigation = (path) => {
    navigate(path);
    setIsMoreMenuOpen(false);
    setIsMobileMenuOpen(false);
  };

  const handleLogout = async () => {
    try {
      sessionStorage.setItem('postLogoutRedirect', '/');
      await signOut();
      setCurrentUser(null);
      navigate('/', { replace: true });
    } catch (error) {
      sessionStorage.removeItem('postLogoutRedirect');
      console.error("Logout failed", error);
    }
  };

  return (
    <>
      <header className="nav-header">
        <div className="nav-header-container">
          <div className="nav-header-logo">
            <div className="nav-header-logo-icon">
              <Icon 
                name="Droplet" 
                onClick={handleIconNavigation}  // Navigation intelligente
                size={24} 
                color="#FFFFFF" 
                style={{ cursor: 'pointer' }}
              />
            </div>
            <span 
              className="nav-header-logo-text"
              onClick={handleIconNavigation}  // Le texte aussi est cliquable
              style={{ cursor: 'pointer' }}
            >
              {APP_NAME}
            </span>
          </div>

          {/* Afficher la navigation seulement si l'utilisateur est connecté */}
          {currentUser && (
            <nav className="nav-header-menu">
              {/* Afficher les éléments principaux (3 premiers + éventuellement l'élément actif) */}
              {mainNavItems.map((item) => (
                <button
                  key={item?.path}
                  onClick={() => handleNavigation(item?.path)}
                  className={`nav-header-menu-item ${isNavItemActive(item) ? 'active' : ''}`}
                >
                  <Icon name={item?.icon} size={16} />
                  {Array.isArray(item?.labelLines) && item.labelLines.length > 0 ? (
                    <span className="nav-header-menu-item-label text-center leading-tight">
                      {item.labelLines.map((line) => (
                        <span key={`${item.path}-${line}`} className="block whitespace-nowrap">
                          {line}
                        </span>
                      ))}
                    </span>
                  ) : (
                    <span>{item?.label}</span>
                  )}
                </button>
              ))}

              {/* Menu "Plus" avec les éléments supplémentaires (si > 0) */}
              {moreNavItems.length > 0 && (
                <div className="more-menu-container relative">
                  <button
                    onClick={() => setIsMoreMenuOpen(!isMoreMenuOpen)}
                    className={`nav-header-menu-item ${isMoreMenuOpen ? 'active' : ''}`}
                    aria-label="Plus d'options"
                  >
                    <Icon name="MoreHorizontal" size={16} />
                    <span>Plus</span>
                  </button>

                  {/* Dropdown menu */}
                  {isMoreMenuOpen && (
                    <div className="absolute top-full right-0 mt-2 w-64 bg-card border-2 border-border rounded-lg shadow-lg py-2 z-50">
                      {moreNavItems.map((item) => (
                        <button
                          key={item?.path}
                          onClick={() => handleNavigation(item?.path)}
                          className={`w-full text-left px-4 py-3 hover:bg-muted/50 transition-colors flex items-center gap-3 ${
                            isNavItemActive(item) ? 'bg-primary/5 text-primary' : ''
                          }`}
                        >
                          <Icon 
                            name={item?.icon} 
                            size={18} 
                            color={isNavItemActive(item) ? 'var(--color-primary)' : 'currentColor'} 
                          />
                          <span className="text-sm font-medium">{item?.label}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </nav>
          )}

          <div className="nav-header-actions">
            {currentUser ? (
              <>
                <Button
                  variant="ghost"
                  size="sm"
                  iconName="User"
                  className="hidden lg:flex"
                >
                  {userName}
                </Button>

                <Button
                  variant="ghost"
                  size="sm"
                  iconName="LogOut"
                  onClick={handleLogout}
                  className="hidden lg:flex"
                >
                  Déconnexion
                </Button>
              </>
            ) : (
              <Button
                variant="default"
                size="sm"
                onClick={() => navigate('/login-authentication')}
                className="hidden lg:flex"
              >
                Connexion
              </Button>
            )}

            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="nav-header-mobile-toggle"
              aria-label="Toggle mobile menu"
            >
              <Icon name={isMobileMenuOpen ? 'X' : 'Menu'} size={24} />
            </button>
          </div>
        </div>
      </header>

      {isMobileMenuOpen && (
        <>
          <div
            className="fixed inset-0 z-[990] bg-background/80 backdrop-blur-sm lg:hidden"
            onClick={() => setIsMobileMenuOpen(false)}
          />
          <div className="nav-mobile-menu z-[1000]">
            <div className="nav-mobile-menu-header">
              <span className="font-semibold text-foreground truncate pr-2">
                {currentUser ? `Bonjour ${userName || 'Client'}` : 'Menu'}
              </span>
              <button
                onClick={() => setIsMobileMenuOpen(false)}
                className="nav-mobile-menu-close"
                aria-label="Close menu"
              >
                <Icon name="X" size={24} />
              </button>
            </div>

            <div className="nav-mobile-menu-items">
              {currentUser ? (
                <>
                  {(isClientRole ? clientMobileMenuItems : currentNavItems).map((item) => (
                    <button
                      key={item?.path}
                      type="button"
                      onClick={() => handleNavigation(item?.path)}
                      className={`nav-mobile-menu-item ${
                        isClientRole
                          ? isClientMobileItemActive(item)
                            ? 'active'
                            : ''
                          : isActivePath(item?.path)
                            ? 'active'
                            : ''
                      }`}
                    >
                      <Icon name={item?.icon} size={20} />
                      <span>{item?.label}</span>
                    </button>
                  ))}

                  <div className="mt-4 pt-4 border-t border-border">
                    <button
                      type="button"
                      onClick={() => {
                        handleLogout();
                        setIsMobileMenuOpen(false);
                      }}
                      className="nav-mobile-menu-item text-error hover:bg-error/10"
                    >
                      <Icon name="LogOut" size={20} />
                      <span>Déconnexion</span>
                    </button>
                  </div>
                </>
              ) : (
                <div className="p-4">
                  <Button
                    variant="default"
                    size="lg"
                    onClick={() => {
                      navigate('/login-authentication');
                      setIsMobileMenuOpen(false);
                    }}
                    className="w-full"
                  >
                    Connexion
                  </Button>
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default RoleBasedHeader;